/* eslint-disable camelcase */
import React from "react";
import { bool, object, array, string } from "prop-types";
import { OtherStory } from "./OtherStory";
import { Link } from "@quintype/components";
import { getStoriesFromCollections, isCollectionEmpty } from "../../utils/collection";
import CollectionTitle from "../../atoms/CollectionTitle";
import { getStoryHeadline, getStoryLabel, isExternalStory } from "../../utils/story";
import { PublishAndReadTime } from "../../atoms/PublishAndReadTime";
import { HeroImage } from "../../atoms/HeroImage";
import { Label } from "../../atoms/Label";
import Headline from "../../atoms/Headline";
import SubHeadline from "../../atoms/SubHeadline";

import "./three-col-nine-stories-var-one-with-bg.m.css";

const ThumbnailStory = ({ story, showSubheadline, showReadTime, showTimestamp, showSeparator, sectionSlug }) => {
  const headline = getStoryHeadline(story);
  const label = getStoryLabel(story);
  const externalStoryUrl = isExternalStory(story);
  const href = externalStoryUrl ? externalStoryUrl : `/${story?.slug}`;
  const target = externalStoryUrl ? "_blank" : "_self";
  return (
    <div styleName="thumbnail-story" style={{ border: `${showSeparator || "none"}` }}>
      <Link href={href} target={target}>
        <HeroImage story={story} aspectRatio={[[4, 3]]} iconSizes={{ height: 24, width: 24 }} />
        {label && <Label story={story} />}

        {headline && <Headline styleName="thumbnail-headline" headline={headline} sectionSlug={sectionSlug} />}
        {showSubheadline && story?.subheadline && (
          <SubHeadline styleName="subheadline" subHeadline={story?.subheadline} sectionSlug={sectionSlug} />
        )}
        <PublishAndReadTime story={story} showReadTime={showReadTime} showTimestamp={showTimestamp} />
      </Link>
    </div>
  );
};

ThumbnailStory.propTypes = {
  story: object,
  showSubheadline: bool,
  showReadTime: bool,
  showTimestamp: bool,
  showSeparator: bool,
  sectionSlug: string,
};

const ThumbnailView = ({
  stories,
  show_story_separator,
  show_timestamp,
  show_read_time,
  show_column_separator,
  sectionSlug,
}) => {
  if (stories.length < 1) {
    return null;
  }
  return (
    <div styleName="thumbnail-container">
      <div styleName="thumbnail-first-row" style={{ border: show_column_separator || "none" }}>
        {stories.slice(0, 2).map((story) => (
          <ThumbnailStory
            key={story.id}
            story={story}
            showSeparator={show_story_separator}
            showTimestamp={show_timestamp}
            showReadTime={show_read_time}
            sectionSlug={sectionSlug}
          />
        ))}
      </div>
      <div styleName="thumbnail-second-row">
        {stories.slice(2, 4).map((story) => (
          <ThumbnailStory
            key={story.id}
            story={story}
            showSeparator={show_story_separator}
            showTimestamp={show_timestamp}
            showReadTime={show_read_time}
            sectionSlug={sectionSlug}
          />
        ))}
      </div>
    </div>
  );
};

ThumbnailView.propTypes = {
  stories: array,
  show_column_separator: bool,
  show_story_separator: bool,
  show_timestamp: bool,
  show_read_time: bool,
  sectionSlug: string,
};

export const ThirdColumn = ({
  collection,
  show_story_separator,
  show_column_separator,
  show_timestamp,
  show_read_time,
  show_row_title,
  enable_thumbnail_view,
  sectionSlug,
}) => {
  const stories = getStoriesFromCollections(collection, 4);
  if (isCollectionEmpty(collection) || stories.length < 1) {
    return null;
  }

  return (
    <div styleName="third-column">
      {show_row_title && (
        <CollectionTitle
          collection={collection}
          containerStyles={"row-title"}
          enableSponsorAd={false}
          sponsorAdDetails={""}
          sectionSlug={sectionSlug}
          sponsorAdConfig={""}
        />
      )}
      {enable_thumbnail_view ? (
        <ThumbnailView
          stories={stories}
          show_story_separator={show_story_separator}
          show_timestamp={show_timestamp}
          show_read_time={show_read_time}
          show_column_separator={show_column_separator}
          sectionSlug={sectionSlug}
        />
      ) : (
        <div styleName="third-column-rows">
          <div styleName="first-row" style={{ border: show_column_separator || "none" }}>
            {stories.slice(0, 2).map((story, index) => (
              <OtherStory
                key={story.id}
                story={story}
                showSeparator={show_story_separator}
                defaultFallback={false}
                showTimestamp={show_timestamp}
                showReadTime={show_read_time}
                showThumbnail={index === 0}
                sectionSlug={sectionSlug}
              />
            ))}
          </div>
          <div styleName="second-row">
            {stories.slice(2, 4).map((story) => (
              <OtherStory
                key={story.id}
                story={story}
                showSeparator={show_story_separator}
                defaultFallback={false}
                showTimestamp={show_timestamp}
                showReadTime={show_read_time}
                showThumbnail={false}
                sectionSlug={sectionSlug}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

ThirdColumn.propTypes = {
  collection: object,
  show_story_separator: bool,
  show_column_separator: bool,
  show_timestamp: bool,
  show_read_time: bool,
  show_row_title: bool,
  enable_thumbnail_view: bool,
  enableSponsorAd: bool,
  sponsorAdDetails: string,
  sectionSlug: string,
};
